// components/LSSF/CityList.jsx
import React, { useState, useEffect } from 'react';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import { ToastContainer, toast } from 'react-toastify';
import axios from 'axios';
import { MdSearch, MdVisibility, MdLocationCity } from 'react-icons/md';

const CityList = () => {
  const base_url = import.meta.env.VITE_LOCAL_BASE_URL;
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCity, setSelectedCity] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [selectedCollege, setSelectedCollege] = useState(null);

  const getCitiesData = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${base_url}/api/v1/colleges`);
      setData(res.data.data);
      
      // Extract unique cities with their states
      const cityMap = new Map();
      res.data.data.forEach(college => {
        if (college.city && college.state) {
          if (!cityMap.has(college.city)) {
            cityMap.set(college.city, {
              city: college.city,
              state: college.state,
              count: 1,
              colleges: [college]
            });
          } else {
            const existing = cityMap.get(college.city);
            existing.count++;
            existing.colleges.push(college);
          }
        }
      });
      
      const citiesList = Array.from(cityMap.values());
      setCities(citiesList);
      setFilteredData(citiesList);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching cities:', err);
      toast.error('Failed to fetch cities');
      setLoading(false);
    }
  };

  useEffect(() => {
    getCitiesData();
  }, []);

  useEffect(() => {
    let filtered = cities;
    
    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase();
      filtered = cities.filter(item => 
        item.city.toLowerCase().includes(searchLower) ||
        item.state.toLowerCase().includes(searchLower)
      );
    }
    
    setFilteredData(filtered);
  }, [searchTerm, cities]);

  const viewCityColleges = (cityData) => {
    setSelectedCollege(cityData);
    setShowModal(true);
  };

  return (
    <div className="page-wrapper">
      <div className="container-fluid">
        <div className="row">
          <div className="col-md-12">
            <div className="card">
              <div className="card-header row d-flex justify-content-between align-items-center">
                <div className="col-md-6">
                  <h4 className="mb-0">City List</h4>
                </div>
                <div className="col-md-6">
                  <div className="input-group">
                    <span className="input-group-text">
                      <MdSearch />
                    </span>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Search by city or state..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </div>
                </div>
              </div>
              <div className="card-body">
                <div className="table-responsive">
                  <table className="table table-bordered shadow-sm mt-3" cellPadding={5}>
                    <thead className="bg-light">
                      <tr>
                        <th>S.No.</th>
                        <th>City</th>
                        <th>State</th>
                        <th>Number of Colleges</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loading ? (
                        <tr>
                          {[...Array(5)].map((_, i) => (
                            <td key={i}><Skeleton count={1} /></td>
                          ))}
                        </tr>
                      ) : filteredData.length > 0 ? (
                        filteredData.map((item, index) => (
                          <tr key={index}>
                            <td>{index + 1}</td>
                            <td>
                              <MdLocationCity className="me-2 text-primary" />
                              <strong>{item.city}</strong>
                            </td>
                            <td>
                              <span className="badge bg-info">{item.state}</span>
                            </td>
                            <td>
                              <span className="badge bg-success p-2">{item.count} Colleges</span>
                            </td>
                            <td>
                              <button 
                                className="btn btn-sm btn-info"
                                onClick={() => viewCityColleges(item)}
                              >
                                <MdVisibility size={18} /> View Colleges
                              </button>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr className="text-center text-danger">
                          <td colSpan={5}>No cities found</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Summary */}
                {!loading && filteredData.length > 0 && (
                  <div className="row mt-4">
                    <div className="col-md-12">
                      <div className="alert alert-info">
                        <strong>Summary:</strong> Total Cities: {filteredData.length} | 
                        Total Colleges: {filteredData.reduce((sum, item) => sum + item.count, 0)}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* City Colleges Modal */}
      {showModal && selectedCollege && (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header bg-info text-white">
                <h5 className="modal-title">
                  Colleges in {selectedCollege.city}, {selectedCollege.state}
                </h5>
                <button type="button" className="btn-close btn-close-white" onClick={() => setShowModal(false)}></button>
              </div>
              <div className="modal-body">
                <div className="table-responsive">
                  <table className="table table-bordered">
                    <thead>
                      <tr>
                        <th>S.No.</th>
                        <th>College Name</th>
                        <th>Type</th>
                        <th>Established</th>
                        <th>Fees</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedCollege.colleges.map((college, idx) => (
                        <tr key={idx}>
                          <td>{idx + 1}</td>
                          <td>{college.collegename}</td>
                          <td>{college.collegetype || '-'}</td>
                          <td>{college.established || '-'}</td>
                          <td>{college.fees || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>Close</button>
              </div>
            </div>
          </div>
        </div>
      )}

      <ToastContainer />
    </div>
  );
};

export default CityList;